import Vue from 'vue'
import VueRouter from 'vue-router'
import { auth, guest, log, permission } from '../middleware/index'

Vue.use(VueRouter)

// Shorthand builders
const pub = (...m) => ({ middleware: [log, ...m] })
const priv = (...m) => ({ middleware: [auth, log, ...m] })
const perm = (p) => ({ middleware: [auth, log, permission], permission: p })

const routes = [
  {
    path: '/',
    name: 'base',
    component: require('@/components/Onboard/Base').default,
    meta: pub()
  },
  {
    path: '/login',
    name: 'login',
    component: require('@/components/Auth/Login').default,
    meta: pub(guest)
  },
  {
    path: '/dashboard',
    component: require('@/components/Layout/DashboardNew.vue').default,
    children: [
      // ── Core ──────────────────────────────────────────────
      {
        path: '',
        name: 'dashboard',
        component: require('@/components/Dashboard/HomeNew').default,
        meta: priv()
      },
      {
        path: 'unauthorized',
        name: 'unauthorized',
        component: require('@/components/Auth/Unauthorized').default,
        meta: priv()
      },
      {
        path: 'error-test',
        name: 'error-test',
        component: require('@/components/_Partials/ErrorTest').default,
        meta: priv()
      },
      {
        path: 'error-example',
        name: 'error-example',
        component: require('@/components/_Partials/ErrorHandlingExample').default,
        meta: priv()
      },

      // ── Settings ──────────────────────────────────────────
      {
        path: 'settings',
        component: require('@/components/Settings/Settings').default,
        children: [
          {
            path: 'config',
            component: require('@/components/Settings/Config').default,
            children: [
              {
                path: 'config_logo',
                name: 'config_logo',
                component: require('@/components/Settings/Tabs/Logo').default,
                meta: perm('setting.logo')
              },
              {
                path: '',
                name: 'config_org',
                component: require('@/components/Settings/Tabs/Organisation').default,
                meta: perm('setting.organisation')
              },
              {
                path: 'config_reg',
                name: 'config_reg',
                component: require('@/components/Settings/Tabs/Registration').default,
                meta: perm('setting.read')
              },
              {
                path: 'price_lists',
                name: 'price_lists',
                component: require('@/components/Settings/Tabs/PriceLists').default,
                meta: perm('setting.price-list')
              },
              {
                path: 'default_messages',
                name: 'default_messages',
                component: require('@/components/Settings/Tabs/DefaultMessages').default,
                meta: perm('setting.default-message')
              },
              {
                path: 'sales_receipts',
                name: 'receipts',
                component: require('@/components/Settings/Tabs/SalesReceipts').default,
                meta: perm('setting.receipt')
              },
              {
                path: 'sales_print_terminals',
                name: 'print_terminals',
                component: require('@/components/Settings/Tabs/SalesPrintTerminals').default,
                meta: perm('setting.read')
              },
              {
                path: 'pos_settings',
                name: 'pos_settings',
                component: require('@/components/Settings/Tabs/POSDefaults').default,
                meta: perm('setting.pos')
              }
            ]
          }
        ]
      },

      // ── Suppliers ─────────────────────────────────────────
      {
        path: '/suppliers',
        component: require('@/components/Supplier/Supplier').default,
        children: [
          {
            path: '',
            name: 'suppliers',
            component: require('@/components/Supplier/Index').default,
            meta: perm('supplier.read')
          },
          {
            path: 'create',
            name: 'create-supplier',
            component: require('@/components/Supplier/Create').default,
            meta: perm('supplier.create')
          },
          {
            path: 'edit/:id',
            name: 'edit-supplier',
            component: require('@/components/Supplier/Edit').default,
            meta: perm('supplier.update')
          },
          {
            path: 'archive',
            name: 'archive',
            component: require('@/components/Supplier/Archive').default,
            meta: perm('supplier.archive')
          }
        ]
      },
      {
        path: '/supplier/history/:id',
        component: require('@/components/Supplier/History').default,
        children: [
          {
            path: '',
            name: 'supplier-history',
            component: require('@/components/Supplier/History/Supplies').default,
            meta: perm('supplier.history')
          },
          {
            path: 'payments',
            name: 'supplier-payments',
            component: require('@/components/Supplier/History/Payments').default,
            meta: perm('supplier.payment')
          }
        ]
      },

      // ── Customers ─────────────────────────────────────────
      {
        path: '/customers',
        component: require('@/components/Customers/Customers').default,
        children: [
          {
            path: '',
            name: 'customers',
            component: require('@/components/Customers/Index').default,
            meta: perm('customer.read')
          },
          {
            path: 'create',
            name: 'create-customer',
            component: require('@/components/Customers/Create').default,
            meta: perm('customer.create')
          },
          {
            path: 'edit/:id',
            name: 'edit-customer',
            component: require('@/components/Customers/Create').default,
            props: true,
            meta: perm('customer.update')
          },
          {
            path: ':id',
            name: 'customer-details',
            component: require('@/components/Customers/CustomerDetails').default,
            props: true,
            meta: perm('customer.read')
          },
          {
            path: 'code/:code',
            name: 'customer-details-by-code',
            component: require('@/components/Customers/CustomerDetails').default,
            props: true,
            meta: perm('customer.read')
          },
          {
            path: 'loyalty/:id',
            name: 'customer-loyalty',
            component: require('@/components/Loyalty/CustomerLoyaltyView').default,
            props: (route) => ({
              customerId: Number(route.params.id),
              customerName: route.query.name || ''
            }),
            meta: perm('loyalty.customers.view')
          },
          {
            path: 'archive',
            name: 'archived-customers',
            component: require('@/components/Customers/Archive').default,
            meta: perm('customer.archive')
          }
        ]
      },

      // ── Supplies / Purchase ───────────────────────────────
      {
        path: '/supplies',
        component: require('@/components/Purchase/Purchase').default,
        children: [
          {
            path: '',
            name: 'supplies',
            component: require('@/components/Purchase/Supplies').default,
            meta: perm('supply.read')
          },
          {
            path: 'create-supply',
            name: 'add-supply',
            component: require('@/components/Purchase/AddSupply').default,
            meta: perm('supply.create')
          },
          {
            path: ':id',
            name: 'purchase-order',
            component: require('@/components/Purchase/PurchaseOrder').default,
            meta: perm('supply.read')
          }
        ]
      },

      // ── Sales ─────────────────────────────────────────────
      {
        path: '/sales',
        component: require('@/components/Sale/Sale').default,
        children: [
          {
            path: '',
            name: 'sales',
            component: require('@/components/Sale/Index').default,
            meta: perm('sale.read')
          }
        ]
      },

      // ── Tickets ───────────────────────────────────────────
      {
        path: '/tickets',
        component: require('@/components/Ticket/Ticket').default,
        children: [
          {
            path: '',
            name: 'tickets',
            component: require('@/components/Ticket/Index').default,
            meta: perm('ticket.read')
          }
        ]
      },

      // ── Accounts ──────────────────────────────────────────
      {
        path: '/account',
        component: require('@/components/Account/Account').default,
        children: [
          {
            path: '',
            name: 'deposit',
            component: require('@/components/Account/Deposit').default,
            meta: perm('deposit.read')
          },
          {
            path: 'withdrawal',
            name: 'withdrawal',
            component: require('@/components/Account/Withdrawal').default,
            meta: perm('withdrawal.read')
          },
          {
            path: 'category',
            name: 'category',
            component: require('@/components/Account/Category').default,
            meta: perm('account.read-category')
          }
        ]
      },
      {
        path: '/account/category/:id',
        component: require('@/components/Account/Category/Category').default,
        children: [
          {
            path: '',
            name: 'subcategories',
            component: require('@/components/Account/Category/Subcategory').default,
            meta: perm('account.read-subcategory')
          }
        ]
      },

      // ── Employees ─────────────────────────────────────────
      {
        path: '/employees',
        component: require('@/components/Employees/Employees').default,
        children: [
          {
            path: '',
            name: 'employees',
            component: require('@/components/Employees/Index').default,
            meta: perm('employee.read')
          },
          {
            path: 'create-employee',
            name: 'add-employee',
            component: require('@/components/Employees/Create').default,
            meta: perm('employee.create')
          },
          {
            path: 'archived-employee',
            name: 'archived-employee',
            component: require('@/components/Employees/Archive').default,
            meta: perm('employee.archive')
          },
          {
            path: 'employee-types',
            name: 'employee-types',
            component: require('@/components/Employees/Type').default,
            meta: perm('employee.type')
          }
        ]
      },
      {
        path: '/employees/:id',
        component: require('@/components/Employees/Employee/Employee').default,
        children: [
          {
            path: '',
            name: 'debt',
            component: require('@/components/Employees/Employee/Debt').default,
            meta: perm('debt.read')
          },
          {
            path: 'archived-debt',
            name: 'archived-debt',
            component: require('@/components/Employees/Employee/ArchivedDebt').default,
            meta: perm('debt.read')
          },
          {
            path: 'employee-payments',
            name: 'employee-payments',
            component: require('@/components/Employees/Employee/Payments').default,
            meta: perm('employees.payment')
          },
          {
            path: 'visualise',
            name: 'employee-visualisation',
            component: require('@/components/Employees/Employee/Visualise').default,
            meta: perm('employee.read')
          }
        ]
      },

      // ── Users ─────────────────────────────────────────────
      {
        path: '/users',
        component: require('@/components/Users/Users').default,
        children: [
          {
            path: '',
            name: 'users',
            component: require('@/components/Users/Index').default,
            meta: perm('user.read')
          },
          {
            path: 'create-user',
            name: 'add-user',
            component: require('@/components/Users/Create').default,
            meta: perm('user.create')
          }
        ]
      },

      // ── Roles & Permissions ───────────────────────────────
      {
        path: '/roles',
        component: require('@/components/Roles/Roles').default,
        children: [
          {
            path: '',
            name: 'role-dashboard',
            component: require('@/components/Roles/Dashboard').default,
            meta: perm('role.read')
          },
          {
            path: 'list',
            name: 'roles',
            component: require('@/components/Roles/Index').default,
            meta: perm('role.read')
          },
          {
            path: 'create',
            name: 'create-role',
            component: require('@/components/Roles/Create').default,
            meta: perm('role.create')
          },
          {
            path: 'permissions',
            name: 'permissions',
            component: require('@/components/Roles/Permissions').default,
            meta: perm('permission.read')
          },
          {
            path: 'permission-manager',
            name: 'permission-manager',
            component: require('@/components/Roles/PermissionManager').default,
            meta: perm('permission.read')
          },
          {
            path: 'user-role-manager',
            name: 'user-role-manager',
            component: require('@/components/Roles/UserRoleManager').default,
            meta: perm('role.assign')
          }
        ]
      },

      // ── Debts ─────────────────────────────────────────────
      {
        path: '/debts',
        component: require('@/components/Debts/Debts').default,
        children: [
          {
            path: '',
            name: 'debts',
            component: require('@/components/Debts/Index').default,
            meta: perm('debt.read')
          },
          {
            path: 'payments',
            name: 'payments',
            component: require('@/components/Debts/Payments').default,
            meta: perm('debt.payment')
          },
          {
            path: 'debt-types',
            name: 'debt-types',
            component: require('@/components/Debts/DebtTypes').default,
            meta: perm('debt.type')
          }
        ]
      },

      // ── Stores / Departments ──────────────────────────────
      {
        path: '/stores',
        component: require('@/components/Departments/Departments').default,
        children: [
          {
            path: '',
            name: 'stores',
            component: require('@/components/Departments/Index').default,
            meta: perm('store.read')
          },
          {
            path: 'create',
            name: 'create-store',
            component: require('@/components/Departments/Create').default,
            meta: perm('store.create')
          }
        ]
      },

      // ── Bookings / Rooms (future) ─────────────────────────
      {
        path: '/bookings',
        component: require('@/components/Bookings/Index').default,
        children: [
          {
            path: '',
            name: 'bookings',
            component: require('@/components/Bookings/BookingList').default,
            meta: priv()
          }
        ]
      },
      {
        path: '/rooms',
        component: require('@/components/Rooms/Index').default,
        children: [
          {
            path: '',
            name: 'rooms',
            component: require('@/components/Rooms/RoomList').default,
            meta: priv()
          }
        ]
      },

      // ── Payment Methods ───────────────────────────────────
      {
        path: '/payment-methods',
        component: require('@/components/PaymentMethod/PaymentMethod').default,
        children: [
          {
            path: '',
            name: 'payment-methods',
            component: require('@/components/PaymentMethod/Index').default,
            meta: perm('payment_method.read')
          }
        ]
      },

      // ── Items ─────────────────────────────────────────────
      {
        path: '/items',
        component: require('@/components/Items/Items').default,
        children: [
          {
            path: '',
            name: 'items',
            component: require('@/components/Items/Index').default,
            meta: perm('item.read')
          },
          {
            path: 'items-archive',
            name: 'items-archive',
            component: require('@/components/Items/Archive').default,
            meta: perm('item.archive')
          },
          {
            path: 'upload-items',
            name: 'upload-items',
            component: require('@/components/Items/RobustUpload').default,
            meta: perm('item.create')
          }
        ]
      },
      {
        path: '/items-vuetify',
        component: require('@/components/Items/ItemsVuetify').default,
        children: [
          {
            path: '',
            name: 'items-vuetify',
            component: require('@/components/Items/IndexVuetify').default,
            meta: perm('item.read')
          }
        ]
      },

      // ── Sessions ──────────────────────────────────────────
      {
        path: '/sessions',
        component: require('@/components/Session/Session').default,
        children: [
          {
            path: '',
            name: 'sessions',
            component: require('@/components/Session/Index').default,
            meta: perm('session.read')
          },
          {
            path: '/create-session',
            name: 'create-sessions',
            component: require('@/components/Session/Create').default,
            meta: perm('session.create')
          }
        ]
      },

      // ── Services ──────────────────────────────────────────
      {
        path: '/services',
        component: require('@/components/Service/Service').default,
        children: [
          {
            path: '',
            name: 'services',
            component: require('@/components/Service/Index').default,
            meta: perm('service.read')
          },
          {
            path: '/create-service',
            name: 'create-service',
            component: require('@/components/Service/Create').default,
            meta: perm('service.create')
          }
        ]
      },

      // ── Categories ────────────────────────────────────────
      {
        path: '/categories',
        component: require('@/components/Category/Category').default,
        children: [
          {
            path: '',
            name: 'categories',
            component: require('@/components/Category/Index').default,
            meta: perm('category.read')
          }
        ]
      },

      // ── Warehouses ────────────────────────────────────────
      {
        path: '/warehouses',
        component: require('@/components/Warehouses/Warehouses').default,
        children: [
          {
            path: '',
            name: 'warehouses',
            component: require('@/components/Warehouses/Index').default,
            meta: perm('warehouse.read')
          },
          {
            path: 'manage/:id',
            name: 'manage-warehouse',
            component: require('@/components/Warehouses/Manage').default,
            meta: perm('warehouse.manage')
          },
          {
            path: 'manage/:id/adjust_stock',
            name: 'warehouse-stock-adjustment',
            component: require('@/components/Warehouses/AdjustWarehouseStock').default,
            meta: perm('warehouse.adjust_stock')
          },
          {
            path: 'manage/:id/stock_adjustment_history',
            name: 'stock-adjustment-history',
            component: require('@/components/Warehouses/StockAdjustmentHistory').default,
            meta: perm('warehouse.history')
          },
          {
            path: 'manage/:id/stock_adjustment_history_items',
            name: 'stock-adjustment-history-items',
            component: require('@/components/Warehouses/StockAdjustmentHistory').default,
            meta: perm('warehouse.history')
          },
          {
            path: 'transfer-history',
            name: 'transfer-history',
            component: require('@/components/Warehouses/History').default,
            meta: perm('warehouse.transfer')
          }
        ]
      },

      // ── Stock ─────────────────────────────────────────────
      {
        path: '/stock-item/:item_id',
        component: require('@/components/Stock/Stock').default,
        children: [
          {
            path: '',
            name: 'stock-item',
            component: require('@/components/Stock/StockItem').default,
            meta: perm('item.stock')
          }
        ]
      },

      // ── Salaries ──────────────────────────────────────────
      {
        path: '/salaries',
        component: require('@/components/Salary/Salary').default,
        children: [
          {
            path: '',
            name: 'salaries',
            component: require('@/components/Salary/Index').default,
            meta: perm('payroll.read')
          },
          {
            path: 'pay',
            name: 'pay-salary',
            component: require('@/components/Salary/Pay').default,
            meta: perm('payroll.create')
          }
        ]
      },

      // ── Discounts ─────────────────────────────────────────
      {
        path: '/discounts',
        component: require('@/components/Discounts/Discount').default,
        children: [
          {
            path: '',
            name: 'discounts',
            component: require('@/components/Discounts/Index').default,
            meta: perm('discount.read')
          },
          {
            path: 'create',
            name: 'create-discount',
            component: require('@/components/Discounts/Modal/CreateEditDiscount').default,
            meta: perm('discount.create')
          }
        ]
      },

      // ── Modifiers ─────────────────────────────────────────
      {
        path: '/modifiers',
        component: require('@/components/Modifiers/Modifier').default,
        children: [
          {
            path: '',
            name: 'modifiers',
            component: require('@/components/Modifiers/Index').default,
            meta: perm('modifier.read')
          },
          {
            path: 'create',
            name: 'create-modifier',
            component: require('@/components/Modifiers/Modal/Create').default,
            meta: perm('modifier.create')
          }
        ]
      },

      // ── Taxes ─────────────────────────────────────────────
      {
        path: '/taxes',
        component: require('@/components/Taxes/Tax').default,
        children: [
          {
            path: '',
            name: 'taxes',
            component: require('@/components/Taxes/Index').default,
            meta: perm('tax.read')
          },
          {
            path: 'create',
            name: 'create-tax',
            component: require('@/components/Taxes/Modal/CreateEditTax').default,
            meta: perm('tax.create')
          }
        ]
      },

      // ── Labels ────────────────────────────────────────────
      {
        path: '/labels',
        component: require('@/components/Items/Items.vue').default,
        children: [
          {
            path: '',
            name: 'labels',
            component: require('@/components/Items/LabelsNew.vue').default,
            meta: perm('item.read')
          },
          {
            path: 'old-labels',
            name: 'old-labels',
            component: require('@/components/Items/Labels.vue').default,
            meta: perm('item.read')
          }
        ]
      },

      // ── POS Terminals ─────────────────────────────────────
      {
        path: '/poses',
        component: require('@/components/POS/POS').default,
        children: [
          {
            path: '',
            name: 'poses',
            component: require('@/components/POS/Index').default,
            meta: perm('pos.read')
          },
          {
            path: 'create',
            name: 'create-pos',
            component: require('@/components/POS/Modal/CreateEditPOS').default,
            meta: perm('pos.create')
          }
        ]
      },

      // ── Reports (legacy) ──────────────────────────────────
      {
        path: '/reports',
        component: require('@/components/Report/Report').default,
        name: 'reports',
        children: [
          {
            path: 'supply-report',
            name: 'supply-report',
            component: require('@/components/Report/SuppliesReport').default,
            meta: perm('report.supply')
          },
          {
            path: 'supplier-payment-report',
            name: 'supplier-payment-report',
            component: require('@/components/Report/SupplierPaymentReport').default,
            meta: perm('report.supplier-payment')
          },
          {
            path: 'payroll-report',
            name: 'payroll-report',
            component: require('@/components/Report/PayrollReport').default,
            meta: perm('report.payroll')
          },
          {
            path: 'employee-debt-report',
            name: 'employee-debt-report',
            component: require('@/components/Report/EmployeeDebtReport').default,
            meta: perm('report.employee-debt')
          },
          {
            path: 'deposit-report',
            name: 'deposit-report',
            component: require('@/components/Report/DepositReport').default,
            meta: perm('report.deposit')
          },
          {
            path: 'withdrawal-report',
            name: 'withdrawal-report',
            component: require('@/components/Report/WithdrawalReport').default,
            meta: perm('report.withdrawal')
          },
          {
            path: 'inventory-report',
            name: 'inventory-report',
            component: require('@/components/Report/InventoryReport').default,
            meta: perm('report.inventory')
          }
        ]
      },

      // ── Reporting (analytics) ─────────────────────────────
      {
        path: '/reporting',
        component: require('@/components/Reporting/Reporting').default,
        children: [
          {
            path: 'sales-summary',
            name: 'sales-summary',
            component: require('@/components/Reporting/SalesSummary').default,
            meta: perm('report.view')
          },
          {
            path: 'sales-by-item',
            name: 'sales-by-item',
            component: require('@/components/Reporting/SalesByItem').default,
            meta: perm('report.view')
          },
          {
            path: 'sales-by-category',
            name: 'sales-by-category',
            component: require('@/components/Reporting/SalesByCategory').default,
            meta: perm('report.view')
          },
          {
            path: 'sales-by-customer',
            name: 'sales-by-customer',
            component: require('@/components/Reporting/SalesByCustomer').default,
            meta: perm('report.view')
          },
          {
            path: 'sales-by-employee',
            name: 'sales-by-employee',
            component: require('@/components/Reporting/SalesByEmployee').default,
            meta: perm('report.view')
          },
          {
            path: 'sales-by-payment-method',
            name: 'sales-by-payment-method',
            component: require('@/components/Reporting/SalesByPaymentMethod').default,
            meta: perm('report.view')
          },
          {
            path: 'sales-by-modifier',
            name: 'sales-by-modifier',
            component: require('@/components/Reporting/SalesByModifier').default,
            meta: perm('report.view')
          },
          {
            path: 'sales-by-variant',
            name: 'sales-by-variant',
            component: require('@/components/Reporting/SalesByVariant').default,
            meta: perm('report.view')
          },
          {
            path: 'query-sales',
            name: 'query-sales',
            component: require('@/components/Reporting/Sales').default,
            meta: perm('report.export')
          },
          {
            path: 'query-data',
            name: 'query-data',
            component: require('@/components/Reporting/Data').default,
            meta: perm('report.export')
          }
        ]
      },

      // ── Loyalty ───────────────────────────────────────────
      {
        path: '/loyalty',
        component: require('@/components/Loyalty/Loyalty').default,
        children: [
          {
            path: '',
            name: 'loyalty-programs',
            component: require('@/components/Loyalty/Index').default,
            meta: perm('loyalty.read')
          },
          {
            path: 'manage/:id',
            name: 'loyalty-program-manage',
            component: require('@/components/Loyalty/ManageProgram').default,
            props: true,
            meta: perm('loyalty.edit')
          }
        ]
      }
    ]
  },
  {
    path: '*',
    redirect: '/'
  }
]

const router = new VueRouter({
  mode: 'hash',
  base: process.env.BASE_URL,
  routes
})

export default router
